// ============================================================================
// dashboardCatalog — blocos configuráveis dos dashboards liberáveis ao
// colaborador (Financeiro e Desempenho). O Master escolhe, por cargo, quais
// blocos aparecem e o ESCOPO dos dados ("all" = empresa/time, "self" = só o
// próprio colaborador).
// ----------------------------------------------------------------------------
// Config armazenada em employee_types/<doc>.dashboards:
//   {
//     financeiro: { enabled: bool, scope: "all"|"self", blocks: [ids] },
//     desempenho: { enabled: bool, scope: "all"|"self"|"specific",
//                   scopeUids: [uid], blocks: [ids] },
//   }
// Escopo dos dados (quem o colaborador enxerga no dashboard):
//   "all"      → toda a empresa/time;
//   "self"     → apenas o próprio colaborador;
//   "specific" → apenas os colaboradores listados em `scopeUids`.
// (O escopo "specific" só se aplica ao dashboard de Desempenho.)
// `teamOnly: true` = bloco que só faz sentido com vários colaboradores; é
// ocultado quando o escopo restringe a um único colaborador.
// ============================================================================

export const FINANCEIRO_BLOCKS = [
  { id: "kpisPrincipais", label: "KPIs principais (receita, pendente, ticket...)" },
  { id: "kpisSecundarios", label: "Indicadores secundários (B2B, B2C, clientes...)" },
  { id: "faturamento", label: "Faturamento diário" },
  { id: "statusPagamento", label: "Status de pagamento" },
  { id: "b2bVsB2c", label: "B2B vs B2C" },
  { id: "porIdioma", label: "Receita por idioma" },
  { id: "pipeline", label: "Pipeline de tradução" },
  { id: "cupons", label: "Top cupons" },
];

export const DESEMPENHO_BLOCKS = [
  { id: "kpis", label: "Indicadores principais" },
  { id: "destaques", label: "Destaques e quem precisa de atenção", teamOnly: true },
  { id: "carga", label: "Carga de trabalho (comparativo)", teamOnly: true },
  { id: "pontualidade", label: "Confiabilidade no prazo (comparativo)", teamOnly: true },
  { id: "evolucao", label: "Evolução (entregas e pontualidade)" },
  { id: "tabela", label: "Tabela de colaboradores", teamOnly: true },
];

export const DASHBOARD_DEFS = {
  financeiro: { label: "Dashboard Financeiro", blocks: FINANCEIRO_BLOCKS },
  desempenho: { label: "Dashboard de Desempenho", blocks: DESEMPENHO_BLOCKS },
};

// Cards da página "Gestão de Desempenho" (visão de equipe, MasterEmployeeDashboard),
// usados como permissão por aba. Granularidade fina: cada card colorido do topo
// é um item, e "Destaques"/"Precisam de atenção" são separados.
export const PERFORMANCE_CARDS = [
  { id: "topTime", label: "Topo · Time (ativos)" },
  { id: "topDisponiveis", label: "Topo · Disponíveis agora" },
  { id: "topAtraso", label: "Topo · Com atraso" },
  { id: "topPontualidade", label: "Topo · Pontualidade do time" },
  { id: "topTempoMedio", label: "Topo · Tempo médio de entrega" },
  { id: "topEntregas", label: "Topo · Páginas no mês" },
  { id: "destaques", label: "Destaques" },
  { id: "atencao", label: "Precisam de atenção" },
  { id: "carga", label: "Carga de trabalho (comparativo)" },
  { id: "pontualidade", label: "Confiabilidade no prazo (comparativo)" },
  { id: "evolucao", label: "Evolução (entregas e pontualidade)" },
  { id: "tabela", label: "Tabela de colaboradores" },
];

// Config default ao habilitar um dashboard (todos os blocos, escopo geral).
export const defaultDashboardConfig = (key) => ({
  enabled: true,
  scope: "all",
  scopeUids: [],
  blocks: (DASHBOARD_DEFS[key]?.blocks || []).map((b) => b.id),
});

// Normaliza a config vinda do Firestore para um objeto seguro de usar.
export const resolveDashboards = (dashboards) => {
  const out = {};
  Object.keys(DASHBOARD_DEFS).forEach((key) => {
    const d = dashboards?.[key] || {};
    const validIds = DASHBOARD_DEFS[key].blocks.map((b) => b.id);
    // "specific" só vale para o Desempenho; nos demais cai para "all".
    let scope = "all";
    if (d.scope === "self") scope = "self";
    else if (d.scope === "specific" && key === "desempenho") scope = "specific";
    out[key] = {
      enabled: !!d.enabled,
      scope,
      scopeUids: Array.isArray(d.scopeUids)
        ? d.scopeUids.filter((u) => typeof u === "string" && u)
        : [],
      blocks: Array.isArray(d.blocks)
        ? d.blocks.filter((id) => validIds.includes(id))
        : validIds,
    };
  });
  return out;
};

// Há algum dashboard habilitado?
export const hasAnyDashboard = (dashboards) => {
  const r = resolveDashboards(dashboards);
  return r.financeiro.enabled || r.desempenho.enabled;
};
